<template>
  <v-card id="documentCard" v-if="doc" :hover="true" :dark="false" class="nohandcursor">
    <!-- <v-card-title> -->
    <v-toolbar flat dense :extended="isClassificationProject">
      <template v-if="isClassificationProject" #extension>
        <CategoryChips
            v-if="isClassificationProject"
            :categories="doc.categories"
            :hideNonCategories="false"
            :ignoreCategories="false"
        ></CategoryChips>
      </template>
      <template #default>
        <v-tooltip bottom open-delay="200">
          <template v-slot:activator="{ on }">
            <span
                v-on="on"
                class="mx-auto"
                :class="displayTitle ? 'doc-title headline' : 'doc-title caption'"
                id="doctitle"
                @dblclick="displayTitle = !displayTitle"
            >
              {{ displayTitle ? doc.title : doc.identifier }}
            </span>
          </template>
          <span>{{ displayTitle ? doc.title : doc.identifier }}</span>
        </v-tooltip>
        <MetadataChipGroup
            v-if="canSeeMetadata"
            id="docmeta"
            :metadata="doc.metadata"
        ></MetadataChipGroup>
        <div v-if="!isQuestionAnsweringProject">
          <v-tooltip v-if="canSeeTest" bottom open-delay="100">
            <template v-slot:activator="{ on }">
              <v-btn
                  @click="openDocInTestView(doc.identifier)"
                  v-on="on"
                  text
                  icon
                  color="primary"
              >
                <v-icon>mdi-eye-check</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("app.label.openInTestView") }}</span>
          </v-tooltip>
        </div>
        <v-tooltip
            v-if="showSegInDoc"
            bottom
            open-delay="200"
        >
          <template v-slot:activator="{ on }">
            <v-btn
                v-on="on"
                text
                icon
                @click="closeDoc()"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("app.button.close") }}</span>
        </v-tooltip>
        <div v-else>
          <v-menu
              v-if="canExportDocuments || canDeleteDocuments || canSeeTest"
              id="menu-doc"
              offset-y
          >
            <template v-slot:activator="{ on }">
              <v-btn
                  v-on="on"
                  text
                  icon
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                  v-if="canDeleteDocuments"
                  :disabled="readOnlyProject || deleting"
                  @click="openConfirmDeleteDocumentDialog = true"
                  id="btn_doc_delete"
              >
                <v-icon class="mr-2">mdi-delete</v-icon>
                <v-list-item-title>{{ $t("document.label.deleteDoc") }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                  v-if="canExportDocuments"
                  @click="exportDoc()"
                  id="btn_doc_export"
              >
                <v-icon class="mr-2">mdi-cloud-download</v-icon>
                <v-list-item-title>{{ $t("document.label.exportDoc") }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                  v-if="canSeeTest && !isQuestionAnsweringProject"
                  @click="openDocInTestView(doc.identifier)"
                  id="btn_doc_export"
              >
                <v-icon class="mr-2">mdi-eye-check</v-icon>
                <v-list-item-title>{{ $t("app.label.openInTestView") }}</v-list-item-title>
              </v-list-item>
              <v-list-item
                  v-if="canModifyMetadata && canSeeMetadata"
                  @click="openAddMetadataDialog = true"
                  id="btn_add-metadata_ToDoc"
              >
                <v-icon class="mr-2">mdi-note-plus</v-icon>
                <v-list-item-title>{{ $t("app.label.addMetadataToThisDoc") }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </v-toolbar>
    <AltTexts :doc="doc"></AltTexts>
    <DeleteConfirmDialog
        v-if="openConfirmDeleteDocumentDialog"
        idHint="document"
        :title="$t('document.deleteDialog.title', { docId: doc.title })"
        :html="$t('document.deleteDialog.message', {
          docId: doc.title,
          annotCnt: doc.annotations ? doc.annotations.length : 0,
        })"
        @no="openConfirmDeleteDocumentDialog = false"
        @yes="removeDoc"
    ></DeleteConfirmDialog>
    <AddMetadataDialog
        :show="openAddMetadataDialog"
        :title="$tc('document.setMetadataSearchDialog.message',1, { count: 1 })"
        @cancel="openAddMetadataDialog=false"
        @done="openAddMetadataDialog=false"
        :currentDoc="doc"
    ></AddMetadataDialog>
    <!-- </v-card-title> -->
    <v-card-text id="documentText" :class="showSegInDoc ? 'text--primary overflow-y-auto' : 'text--primary'" :style="showSegInDoc ? 'max-height: 75vh' : ''">
      <div
          :class="{
            selectedLabelText: !readOnlyProject && selectedLabel && !isClassificationProject,
            rtl: isDocumentLangageRightToLeft,
            [`font-${projectLanguage}`]: true,
          }"
          :style="`padding: 0.5em; font-size: large; white-space: pre-wrap; letter-spacing: 0.0em !important; line-height: 1.75rem;`"
          v-bind:kt:docid="doc.identifier"
          v-bind:kt:start="start"
          v-bind:kt:end="end"
          @mouseenter="enterAnnotationArea()"
          @mouseleave="leaveAnnotationArea()"
          @mousedown="startTextSelection()"
          @mouseup="endTextSelection()"
          @dblclick="handleSelectedText()"
          v-html="doc.highlight ? doc.highlight.text : doc.text"
          :id="containerId"
      ></div>
    </v-card-text>
    <!-- Using a renderless component because it is impossible to track changes made in the annotation array by the vuex store -->
    <!-- FIXME?: replace the v-bind:annot with a :annot -->
    <RenderlessAnnotation
        v-for="annot in filterAnnotationsAccordingToCurrentLayout(doc.annotations)"
        v-bind:annot="annot"
        :key="annot.identifier"
        :containerId="containerId"
        :containerMounted="mounted"
    ></RenderlessAnnotation>
  </v-card>
</template>

<script>
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import DeleteConfirmDialog from "../utils/DeleteConfirmDialog";
import AltTexts from "./AltTexts";
import RenderlessAnnotation from "../RenderlessAnnotation";
import CategoryChips from "../categories/CategoryChips";
import MetadataChipGroup from "../MetadataChipGroup";
import annotationContainerMixin from "../../mixins/annotationContainerMixin";
import languageMixin from "../../mixins/languageMixin";
import tourMixin from "../../tourUtils/tourMixin";
import { openDocInTestView } from "@/routes/testRoutes";
import AddMetadataDialog from "@/views/AddMetadataDialog";
import VueScrollTo from "vue-scrollto";

const disableMouseenterTimeout = 500;
const enableMouseenterTimeout = 2000;

export default {
  props: ["doc", "showSegInDoc", "scrollCard"],
  mixins: [annotationContainerMixin, languageMixin],
  components: {
    RenderlessAnnotation,
    CategoryChips,
    MetadataChipGroup,
    DeleteConfirmDialog,
    AltTexts,
    AddMetadataDialog
  },
  data() {
    return {
      disableMouseenter: true,
      // required by the RenderlessAnnotation
      mounted: false,
      // required by the annotationContainer mixin
      displayTitle: true,
      openConfirmDeleteDocumentDialog: false,
      openAddMetadataDialog: false,
      deleting: false,
      downloading: false,
      containerId: "doctext",
    };
  },
  created() {
    tourMixin.decorate(this, "maybeCreateAnnotation");
    tourMixin.decorate(this, "maybeDeleteAnnotation");
  },
  beforeMount() {
    this.setDocumentLanguage(this.doc?.metadata?.language)
  },
  mounted() {
    console.debug("DocumentCard mounted");
    this.mounted = true;
    const self = this;
    if (this.doc.categories) {
      this.doc.categories.forEach(category => {
        const categoLabel = this.labelsByName[category.labelName]
        category.labelSetName = categoLabel?.labelSetName
      })
    }
    setTimeout(() => {
      self.disableMouseenter = false;
      self.scrollToSegmentInDocument()
    }, disableMouseenterTimeout);
  },
  beforeDestroy() {
    console.debug("DocumentCard destroyed");
    this.mounted = false;
  },
  computed: {
    ...mapState("settings", ["labelsByName"]),
    ...mapGetters("settings", ["miniNav", "selectedLabel", "filterAnnotationsAccordingToCurrentLayout"]),
    ...mapGetters([
      "isClassificationProject",
      "isQuestionAnsweringProject",
      "readOnlyProject",
      "projectLanguage",
    ]),
    ...mapGetters("auth", [
      "canDeleteDocuments",
      "canExportDocuments",
      "canSeeTest",
      "canSeeMetadata",
      "canModifyMetadata"
    ]),
    start() {
      return this.doc.shift ? -this.doc.shift : 0;
    },
    end() {
      return this.doc.text.length - (this.doc.shift ? this.doc.shift : 0);
    },
    ...mapGetters("settings", ["dark", "textColor", "toolbarColor"]),
    ...mapGetters("auth", ["canDeleteDocuments", "canExportDocuments"]),
    ...mapGetters("document", ["docDeletedParams"]),
    isDocumentLangageRightToLeft() {
      if (this.doc && this.doc.metadata && this.doc.metadata["language"]) {
        return ["ar", "ur", "fa", "ku"].includes(
            this.doc.metadata["language"].toLowerCase()
        );
      } else {
        return this.containsCharactersFromRightToLeftLanguage(
            this.doc.text.slice(0, 256)
        );
      }
    },
  },
  methods: {
    openDocInTestView,
    ...mapMutations(["setHiddenLeftDrawer", "setShowSegmentInDocument"]),
    ...mapActions("document", [
      "createAnnotation",
      "deleteAnnotation",
      "rejectAnnotation",
      "exportDocument",
      "deleteDocument",
    ]),
    // required by the mixin
    maybeCreateAnnotation(annotation) {
      return this.createAnnotation(annotation);
    },
    // required by the mixin
    maybeDeleteAnnotation(annotation) {
      return this.deleteAnnotation(annotation);
    },
    // required by the mixin
    maybeRejectAnnotation(annotation) {
      return this.rejectAnnotation(annotation);
    },
    mouseenterValidate() {
      if (!this.disableMouseenter) {
        this.hideNonCategories = true;
      } else {
        const self = this;
        this.$validateTimeout = setTimeout(() => {
          self.mouseenterValidate();
        }, enableMouseenterTimeout);
      }
    },
    mouseleaveValidate() {
      this.hideNonCategories = false;
      if (this.$validateTimeout) {
        clearTimeout(this.$validateTimeout);
      }
      this.$validateTimeout = null;
    },
    mouseenterSkip() {
      if (!this.disableMouseenter) {
        this.ignoreCategories = true;
      } else {
        const self = this;
        this.$skipTimeout = setTimeout(() => {
          self.mouseenterSkip();
        }, enableMouseenterTimeout);
      }
    },
    mouseleaveSkip() {
      this.ignoreCategories = false;
      if (this.$skipTimeout) {
        clearTimeout(this.$skipTimeout);
      }
      this.$skipTimeout = null;
    },
    closeDoc() {
      this.setShowSegmentInDocument(false)
      this.setHiddenLeftDrawer(false)
    },
    scrollToSegmentInDocument() {
      const element = "#highlightedSegment"
      const duration = 750
      const options = { container: this.scrollCard ? "#documentText" : "body", offset: this.scrollCard ? -76 : -200 } // offset = toolbar size
      console.log("toto")
      VueScrollTo.scrollTo(element, duration, options)
    },
    async removeDoc() {
      this.deleting = true;
      try {
        await this.deleteDocument(this.doc.identifier);
        this.openConfirmDeleteDocumentDialog = false;
        const searchParams = this.docDeletedParams();
        this.$router.push({
          name: "documentsByParams",
          params: { searchParams },
        });
      } finally {
        this.deleting = false;
      }
    },
    async exportDoc() {
      this.downloading = true;
      try {
        await this.exportDocument(this.doc.identifier);
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style>
.nohandcursor {
  cursor: default !important;
}
.selectedLabelText {
  cursor: text !important;
}
.kt_sent > a.kt_del_an {
  display: none;
}
</style>
